import { DnsOutlined, Inventory2Outlined, LinkOutlined, PlaceOutlined, SensorsOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, FormRowSwitch, MyModal, SiteSelect, TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { BoxModel } from "types";

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

interface IEdit {
  box: BoxModel;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EditBox({ box, onClose, onSuccess }: IEdit) {
  const [state, setState] = useState({
    deviceId: box.deviceId || "",
    siteId: box.siteId || box.site?._id || "",
    name: box.name || "",
    frameServer: box.frameServer || "",
    healthUrl: box.healthUrl || "",
    recordVideo: Boolean(box.recordVideo)
  });
  const [errors, setErrors] = useState({
    deviceId: "",
    siteId: "",
    name: "",
    frameServer: "",
    healthUrl: ""
  });

  const t = useTranslation();
  const disabledSubmit = useMemo(() => !!(errors.deviceId || !state.deviceId), [errors, state]);
  const { isLoading, response, error, callFetch: callFetchPut } = useFetch("PUT");
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      const body: Record<string, string | boolean> = {};
      if (box.deviceId !== state.deviceId) {
        body.deviceId = state.deviceId;
      }
      if (box.name !== state.name) {
        body.name = state.name;
      }
      if (box.recordVideo !== state.recordVideo) {
        body.recordVideo = state.recordVideo;
      }
      const prevFrameServer = box.frameServer || "";
      if (prevFrameServer !== state.frameServer) {
        body.frameServer = state.frameServer;
      }
      const prevHealthUrl = box.healthUrl || "";
      if (prevHealthUrl !== state.healthUrl) {
        body.healthUrl = state.healthUrl;
      }
      if (Object.keys(body).length === 0) {
        enqueueSnackbar(t["Nothing to update"] || "Nothing to update", { variant: "info" });
        return;
      }
      callFetchPut(ENDPOINTS.boxes + `/${box._id}`, {
        body: JSON.stringify(body)
      }).catch(console.error);
    },
    [state, box, callFetchPut, enqueueSnackbar, t]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Edit smart box"]}
      subtitle={t["Edit Surveillance Box description"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            value={state.deviceId}
            label={t["Device ID"]}
            type="text"
            placeholder={t["e.g A2:4D:23:56:67"]}
            error={!!errors.deviceId}
            helperText={errors.deviceId || t["Device ID helper"]}
            startAdornment={<SensorsOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, deviceId: value }));
              setErrors((prevState) => ({ ...prevState, deviceId: "" }));
            }}
            fullWidth
            required
            autoFocus
          />
          <TextInput
            value={state.name}
            label={t["Box Name"]}
            type="text"
            placeholder={t["e.g Aeyron Office NSTP"]}
            error={!!errors.name}
            helperText={errors.name}
            startAdornment={<Inventory2Outlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, name: value }));
              setErrors((prevState) => ({ ...prevState, name: "" }));
            }}
            fullWidth
            required
          />
          <Box>
            <SiteSelect
              value={state.siteId || undefined}
              disabled
              startAdornment={<PlaceOutlined sx={iconSx} />}
              onChange={() => {}}
            />
            <Box sx={{ mt: 0.75, px: 0.5 }}>
              <Typography variant="caption" sx={{ color: COLORS.surface.secondaryText, lineHeight: 1.5 }}>
                {t["Site helper"]}
              </Typography>
            </Box>
          </Box>
          <TextInput
            value={state.frameServer}
            label={t["Frame Server"]}
            type="text"
            placeholder={t["e.g server1"]}
            error={!!errors.frameServer}
            helperText={errors.frameServer || t["Frame Server helper"]}
            startAdornment={<DnsOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, frameServer: value }));
              setErrors((prevState) => ({ ...prevState, frameServer: "" }));
            }}
            fullWidth
          />
          <TextInput
            value={state.healthUrl}
            label={t["Health URL"] || "Health URL"}
            type="url"
            placeholder="https://example.com/"
            error={!!errors.healthUrl}
            helperText={
              errors.healthUrl ||
              t["Health URL helper"] ||
              "Public HTTPS tunnel URL used to show Tunnel Healthy/Down"
            }
            startAdornment={<LinkOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, healthUrl: value }));
              setErrors((prevState) => ({ ...prevState, healthUrl: "" }));
            }}
            fullWidth
          />
          <FormRowSwitch
            label={t["Record Video"]}
            description={t["Record Video helper"]}
            checked={state.recordVideo}
            onChange={(checked) => setState((prevState) => ({ ...prevState, recordVideo: checked }))}
          />
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Update"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
